import React, { useEffect, useState } from "react";
import { ModalProps } from "react-bootstrap";
import { Plus, Trash, X } from "react-bootstrap-icons";
import { BaseButton, Error, TextAreaInput } from "../../../../libs";
import { ReferralNote, ReferralParticipant } from "../../types";
import * as Styled from "./ParticipantNotesModal.styled";

interface Props extends ModalProps {
  participant: ReferralParticipant | null;
  onAdd: (participantId: string, text: string) => Promise<ReferralNote>;
  onDelete: (participantId: string, noteId: string) => Promise<void>;
}

export const ParticipantNotesModal: React.FC<Props> = ({
  participant,
  onAdd,
  onDelete,
  ...props
}) => {
  const [notes, setNotes] = useState<ReferralNote[]>([]);
  const [text, setText] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  useEffect(() => {
    if (props.show) {
      setNotes(participant?.notes || []);
      setText("");
      setError("");
    }
  }, [props.show, participant]);

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    const value = text.trim();
    if (!participant || !value) {
      setError("* Write a note before adding it");
      return;
    }
    if (value.length > 2000) {
      setError("* Notes cannot exceed 2,000 characters");
      return;
    }
    setSaving(true);
    try {
      await onAdd(participant.id, value);
      setText("");
    } catch {
      /* The parent displays the API error. */
    } finally {
      setSaving(false);
    }
  };

  const remove = async (noteId: string) => {
    if (!participant) return;
    setDeletingId(noteId);
    try {
      await onDelete(participant.id, noteId);
    } catch {
      /* The parent displays the API error. */
    } finally {
      setDeletingId("");
    }
  };

  return (
    <Styled.NotesModal {...props} centered keyboard={false}>
      <Styled.ModalWrapper>
        <Styled.Header>
          <div>
            <Styled.ModalTitle>Referrer Notes</Styled.ModalTitle>
            <Styled.Subtitle>{participant?.email}</Styled.Subtitle>
          </div>
          <Styled.CloseButton type="button" onClick={() => props.onHide?.()} aria-label="Close">
            <X size={22} />
          </Styled.CloseButton>
        </Styled.Header>
        <form onSubmit={add}>
          {error && <Error message={error} />}
          <TextAreaInput
            fieldName="referrerNote"
            fieldLabel="New Note"
            placeholder="Add context, payout details, or a follow-up reminder…"
            fieldValue={text}
            onChangeHandler={(event) => {
              setText((event.target as HTMLTextAreaElement).value);
              setError("");
            }}
            rows={3}
          />
          <Styled.AddRow>
            <Styled.CharacterCount>{text.length} / 2,000</Styled.CharacterCount>
            <Styled.AddButton>
              <BaseButton type="submit" disabled={saving}>
                <Plus /> {saving ? "Adding..." : "Add Note"}
              </BaseButton>
            </Styled.AddButton>
          </Styled.AddRow>
        </form>
        <Styled.Divider />
        {notes.length === 0 ? (
          <Styled.EmptyNotes>No notes have been added for this referrer.</Styled.EmptyNotes>
        ) : (
          <Styled.NotesGrid>
            {[...notes].reverse().map((note, index) => (
              <Styled.StickyNote key={note.id} $variant={index % 3}>
                <Styled.NoteText>{note.text}</Styled.NoteText>
                <Styled.NoteFooter>
                  <span>{new Date(note.createdAt).toLocaleString()}</span>
                  <Styled.DeleteButton
                    type="button"
                    onClick={() => remove(note.id)}
                    disabled={deletingId === note.id}
                    title="Delete note"
                  >
                    <Trash />
                  </Styled.DeleteButton>
                </Styled.NoteFooter>
              </Styled.StickyNote>
            ))}
          </Styled.NotesGrid>
        )}
      </Styled.ModalWrapper>
    </Styled.NotesModal>
  );
};
